import { useTheme } from "@mui/material";
import { tokens } from "../../theme";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TakeoutDiningIcon from "@mui/icons-material/TakeoutDining";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PaymentsIcon from "@mui/icons-material/Payments";
import BarChartIcon from "@mui/icons-material/BarChart";
import StatBox from "../../components/statBox";
import BarChart from "../../components/barChart";
import { useResponsive } from "../../hooks/uiHook";
import { useRestaurant } from "../../hooks/RestaurantContext";
import { DashboardCard, ChartCard } from "../../components/dashboardCard";
import { useTranslation } from "react-i18next";

const safe = (val, fallback = 0) => (typeof val === "number" ? val : fallback);

const formatChange = (filter, percentageChange) => {
  if (filter === "custom" || typeof percentageChange !== "number") return "0%";
  const sign = percentageChange >= 0 ? "+" : "";
  return `${sign}${percentageChange.toFixed(1)}%`;
};

const shareLabel = (part, total, t) =>
  total > 0
    ? t("dashboard.ofTotal", { value: ((part / total) * 100).toFixed(1) })
    : t("dashboard.ofTotal", { value: "0" });

const FirstRow = ({ statistics, filter }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { currency } = useRestaurant();
  const { isSmallScreen } = useResponsive();
  const col = isSmallScreen ? "1 / -1" : "span 3";
  const chartCol = isSmallScreen ? "1 / -1" : "span 8";

  const totalOrders = safe(statistics.totalOrders);
  const surPlaceCount = safe(statistics.deliveryTypes?.surPlaceCount);
  const emporterCount = safe(statistics.deliveryTypes?.emporterCount);
  const surPlace = safe(statistics.deliveryTypes?.surPlace);
  const emporter = safe(statistics.deliveryTypes?.emporter);
  const deliveryTotal = surPlace + emporter;
  const cb = safe(statistics.paymentMethodsTotalRevenue?.cb);
  const espece = safe(statistics.paymentMethodsTotalRevenue?.espece);
  const paymentTotal = cb + espece;
  const cbCount = safe(statistics.paymentMethodsTotalRevenue?.cbCount);
  const especeCount = safe(statistics.paymentMethodsTotalRevenue?.especeCount);
  const revenueChange = statistics.revenueComparison?.percentageChange;
  const revenueUp = typeof revenueChange === "number" ? revenueChange >= 0 : true;

  const cards = [
    {
      title: `${safe(statistics.totalRevenue)} ${currency}`,
      subtitle: t("dashboard.sales"),
      increase: formatChange(filter, revenueChange),
      trend: revenueUp ? "up" : "down",
      icon: <PointOfSaleIcon />,
      iconColor: revenueUp ? colors.greenAccent[500] : colors.redAccent[500],
    },
    {
      title: `${totalOrders}`,
      subtitle: t("dashboard.orderCount"),
      icon: <ReceiptLongIcon />,
      iconColor: colors.blueAccent[300],
    },
    {
      title: `${surPlaceCount}`,
      subtitle: t("dashboard.dineInCount"),
      progress: totalOrders > 0 ? surPlaceCount / totalOrders : 0,
      increase: shareLabel(surPlaceCount, totalOrders, t),
      trend: "neutral",
      icon: <StorefrontIcon />,
      iconColor: colors.greenAccent[500],
    },
    {
      title: `${emporterCount}`,
      subtitle: t("dashboard.takeawayCount"),
      progress: totalOrders > 0 ? emporterCount / totalOrders : 0,
      increase: shareLabel(emporterCount, totalOrders, t),
      trend: "neutral",
      icon: <TakeoutDiningIcon />,
      iconColor: colors.redAccent[400],
    },
    {
      title: `${surPlace} ${currency}`,
      subtitle: t("dashboard.dineInRevenue"),
      progress: deliveryTotal > 0 ? surPlace / deliveryTotal : 0,
      increase: shareLabel(surPlace, deliveryTotal, t),
      trend: "neutral",
      icon: <StorefrontIcon />,
      iconColor: colors.greenAccent[500],
    },
    {
      title: `${emporter} ${currency}`,
      subtitle: t("dashboard.takeawayRevenue"),
      progress: deliveryTotal > 0 ? emporter / deliveryTotal : 0,
      increase: shareLabel(emporter, deliveryTotal, t),
      trend: "neutral",
      icon: <TakeoutDiningIcon />,
      iconColor: colors.redAccent[400],
    },
    {
      title: `${cb} ${currency}`,
      subtitle: t("dashboard.cardRevenue"),
      progress: paymentTotal > 0 ? cb / paymentTotal : 0,
      increase: `${cbCount} · ${shareLabel(cb, paymentTotal, t)}`,
      trend: "neutral",
      icon: <CreditCardIcon />,
      iconColor: colors.blueAccent[200],
    },
    {
      title: `${espece} ${currency}`,
      subtitle: t("dashboard.cashRevenue"),
      progress: paymentTotal > 0 ? espece / paymentTotal : 0,
      increase: `${especeCount} · ${shareLabel(espece, paymentTotal, t)}`,
      trend: "neutral",
      icon: <PaymentsIcon />,
      iconColor: colors.greenAccent[400],
    },
  ];

  return (
    <>
      {cards.map((card) => (
        <DashboardCard
          key={card.subtitle}
          sx={{
            gridColumn: col,
            display: "flex",
            alignItems: "center",
          }}
        >
          <StatBox {...card} />
        </DashboardCard>
      ))}
      <ChartCard
        gridColumn={chartCol}
        gridRow="span 2"
        title={t("dashboard.topProducts")}
        icon={<BarChartIcon color="primary" />}
      >
        <BarChart
          data={
            statistics.topProducts
              ? statistics.topProducts.map((prod) => ({
                  category: prod.name,
                  quantity: prod.totalCount,
                }))
              : []
          }
        />
      </ChartCard>
    </>
  );
};

export default FirstRow;
